import React, { memo } from "react";
import { Alert, AlertProps, Collapse, Typography, Box } from "@mui/material";
import {
  ErrorCategory,
  extractErrorDetails,
} from "../../utils/errors/errorHandler";

/**
 * Standardized error display component with memoization
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Consistent styling for different error types
 * - Visual differentiation between error categories
 * - Field error support
 * - Animation for better UX
 */

interface ErrorDisplayProps {
  error: unknown;
  onClose?: () => void;
  showIcon?: boolean;
  fullWidth?: boolean;
  variant?: AlertProps["variant"];
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onClose,
  showIcon = true,
  fullWidth = true,
  variant = "filled",
}) => {
  // If no error, don't render anything
  if (!error) return null;

  // Process the error to get standardized details
  const { message, category, fields } = extractErrorDetails(error);

  // Map error category to alert severity
  const getSeverity = (category: ErrorCategory): AlertProps["severity"] => {
    switch (category) {
      case ErrorCategory.VALIDATION:
        return "warning";
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.AUTHORIZATION:
        return "error";
      case ErrorCategory.NETWORK:
      case ErrorCategory.SERVER:
        return "error";
      case ErrorCategory.NOT_FOUND:
        return "info";
      default:
        return "error";
    }
  };

  return (
    <Collapse in={!!error} timeout={300}>
      <Alert
        severity={getSeverity(category)}
        variant={variant as AlertProps["variant"]}
        onClose={onClose}
        icon={showIcon}
        sx={{
          width: fullWidth ? "100%" : "auto",
          mb: 2,
        }}
      >
        <Typography variant="body2" fontWeight="medium">
          {message}
        </Typography>

        {/* Display field errors if available */}
        {fields && Object.keys(fields).length > 0 && (
          <Box sx={{ mt: 1 }}>
            {Object.entries(fields).map(([field, fieldError]) => (
              <Typography
                key={field}
                variant="caption"
                component="div"
                fontWeight="normal"
                sx={{ mt: 0.5 }}
              >
                <strong>{field}:</strong> {fieldError}
              </Typography>
            ))}
          </Box>
        )}
      </Alert>
    </Collapse>
  );
};

// Memoize the component to prevent unnecessary re-renders
const MemoizedErrorDisplay = memo(ErrorDisplay, (prevProps, nextProps) => {
  // Custom comparison function for the error object
  // Only re-render when the error changes meaningfully
  if (!prevProps.error && !nextProps.error) return true; // Both null/undefined
  if (!prevProps.error || !nextProps.error) return false; // One is null

  const prevDetails = extractErrorDetails(prevProps.error);
  const nextDetails = extractErrorDetails(nextProps.error);

  // Compare relevant error details
  return (
    prevDetails.message === nextDetails.message &&
    prevDetails.category === nextDetails.category &&
    JSON.stringify(prevDetails.fields) === JSON.stringify(nextDetails.fields)
  );
});

export { MemoizedErrorDisplay as ErrorDisplay };
export default MemoizedErrorDisplay;
